import Cookies from 'js-cookie'
import { ThemeProvider as NextThemesProvider, useTheme } from 'next-themes'
import { useEffect, type ReactNode } from 'react'

export const THEME_STORAGE_KEY = '@Habitar:theme'

export function migrateLegacyThemePreference() {
  try {
    if (localStorage.getItem(THEME_STORAGE_KEY)) return
    const legacy = Cookies.get(THEME_STORAGE_KEY)
    if (legacy === 'light' || legacy === 'dark') localStorage.setItem(THEME_STORAGE_KEY, legacy)
  } catch {
    return
  }
}

function resolvedCssColor(value: string): string | null {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = 1
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return null
  context.fillStyle = value
  context.fillRect(0, 0, 1, 1)
  const [r, g, b] = context.getImageData(0, 0, 1, 1).data
  return `#${[r, g, b].map(channel => channel.toString(16).padStart(2, '0')).join('')}`
}

function ThemeColorMeta() {
  const { resolvedTheme } = useTheme()

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const channels = getComputedStyle(document.documentElement).getPropertyValue('--background').trim()
      if (!channels) return
      const color = resolvedCssColor(`oklch(${channels})`)
      if (!color) return
      const metas = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
      if (metas.length === 0) {
        const meta = document.createElement('meta')
        meta.name = 'theme-color'
        document.head.appendChild(meta)
        meta.content = color
        return
      }
      metas.forEach(meta => {
        meta.content = color
      })
    })
    return () => cancelAnimationFrame(frame)
  }, [resolvedTheme])

  return null
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  return (
    <NextThemesProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange storageKey={THEME_STORAGE_KEY}>
      <ThemeColorMeta />
      {children}
    </NextThemesProvider>
  )
}
